/**
 * Runs writes one at a time, in the order they were asked for.
 *
 * The request-copy endpoint takes the whole document, so two overlapping
 * writes do not merge — the later one wins wholesale, whichever it happens to
 * be. That makes "reset the SMS, then edit the email" a race rather than a
 * sequence, and the loser is silent: the reset appears to work and is undone.
 *
 * Cancelling a queued autosave before a reset fixes that ONE pair. It does not
 * fix an edit made while the reset is still in flight, which simply starts a
 * new write that races it (Greptile, #2942). A queue fixes the class.
 */
export const createWriteQueue = () => {
  let tail: Promise<unknown> = Promise.resolve();

  return <T,>(operation: () => Promise<T>): Promise<T> => {
    const next = tail.then(operation);
    /**
     * The tail is always a RESOLVED promise, which does two jobs with one
     * line: a failed write cannot wedge the queue and strand everything
     * behind it, and the chain's own copy of the rejection is handled, so
     * Node does not report it as unhandled while the caller is dealing with it.
     *
     * An earlier version also passed `operation` as the rejection handler
     * above. That was a second way of achieving the first job, and having both
     * meant neither could be tested: removing either one left the other
     * covering for it, so no test could tell the difference.
     */
    tail = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  };
};
